// src/components/modals/CreateFolderModal.jsx
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import useModalHistory from '../../../hooks/useModalHistory';

const CreateFolderModal = ({ show, onHide, onCreate, currentPath }) => {
  const [folderName, setFolderName] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

    useModalHistory(show, onHide, 'files-addFolder-modal');

  
  const inputRef = useRef(null);
  
  // Clear input and focus when modal opens
  useEffect(() => {
    if (show) {
      setFolderName('');
      setError('');
      
      // Focus input with a slight delay to ensure modal is fully rendered
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 100);
    }
  }, [show]);
  
  // Handle input change
  const handleChange = (e) => {
    setFolderName(e.target.value);
    
    // Clear error when user types
    if (error) {
      setError('');
    }
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate folder name
    if (!folderName.trim()) {
      setError('Please enter a folder name');
      return;
    }
    
    // Check for invalid characters in folder name
    const invalidChars = /[\\/:*?"<>|]/;
    if (invalidChars.test(folderName)) {
      setError('Folder name cannot contain these characters: \\ / : * ? " < > |');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      await onCreate(folderName.trim());
      onHide();
    } catch (error) {
      setError(error.message || 'Failed to create folder');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Get current folder display name
  const getCurrentFolderDisplay = () => {
    if (currentPath.length === 0) {
      return "All Files";
    }
    return currentPath[currentPath.length - 1];
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      backdrop="static"
    >
      <Modal.Header closeButton>
        <Modal.Title id="createFolderModalLabel">
          Create Folder in {getCurrentFolderDisplay()}
        </Modal.Title>
      </Modal.Header>
      
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && (
            <Alert variant="danger" className="mb-3">
              {error}
            </Alert>
          )}
          
          <Form.Group controlId="folderNameInput">
            <Form.Label>Folder Name</Form.Label>
            <Form.Control
              ref={inputRef}
              type="text"
              placeholder="Enter folder name"
              value={folderName}
              onChange={handleChange}
              autoComplete="off"
              disabled={isSubmitting}
            />
          </Form.Group>
        </Modal.Body>
        
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={onHide}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button 
            variant="success" 
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                Creating...
              </>
            ) : 'Create Folder'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

CreateFolderModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
  currentPath: PropTypes.array.isRequired
};

export default CreateFolderModal;